'use client';

/**
 * @description Segmented toggle switch for Mission Control context switching (Hiring / Job search).
 * Renders a dark pill with a green gradient active segment for clear visual feedback.
 */

interface MissionControlSwitchProps {
  /** Which segment is currently active. */
  activeContext: 'company' | 'candidate';
  /** Label for the "Hiring" (company) segment. */
  hireLabel: string;
  /** Label for the "Job search" (candidate) segment. */
  jobSearchLabel: string;
  /** Called when the inactive segment is clicked. */
  onSwitch: () => void;
}

export const MissionControlSwitch = ({
  activeContext,
  hireLabel,
  jobSearchLabel,
  onSwitch,
}: MissionControlSwitchProps) => {
  const isHireActive = activeContext === 'company';

  return (
    <div
      className="inline-flex w-fit items-center self-start rounded-xl bg-surface-150 p-1"
      role="group"
      aria-label="Switch context"
      data-test-id="sidebar-switch-context"
    >
      {/* Hiring segment */}
      <button
        type="button"
        onClick={isHireActive ? undefined : onSwitch}
        aria-pressed={isHireActive}
        aria-current={isHireActive ? 'true' : undefined}
        className={
          isHireActive
            ? 'rounded-xl bg-gradient-to-r from-green-600 to-green-400 px-4 py-1.5 text-xs font-bold text-white shadow transition-all'
            : 'rounded-xl px-4 py-1.5 text-xs font-bold text-ink/60 transition-all hover:text-ink/90'
        }
      >
        {hireLabel}
      </button>

      {/* Job search segment */}
      <button
        type="button"
        onClick={isHireActive ? onSwitch : undefined}
        aria-pressed={!isHireActive}
        aria-current={!isHireActive ? 'true' : undefined}
        className={
          !isHireActive
            ? 'rounded-xl bg-gradient-to-r from-green-600 to-green-400 px-4 py-1.5 text-xs font-bold text-white shadow transition-all'
            : 'rounded-xl px-4 py-1.5 text-xs font-bold text-ink/60 transition-all hover:text-ink/90'
        }
      >
        {jobSearchLabel}
      </button>
    </div>
  );
};
